import { pb } from "../pocketbase/pocketbase";
import {
  TrainingVersion,
  TrainingVersionFormData,
} from "../../core/trainingVersion/entities/TrainingVersion";
import type { RepositoryInterface } from "../interfaces/RepositoryInterface";

const collection = "trainingVersions";

function relationIds(value: unknown): string[] {
  if (!value || !Array.isArray(value)) return [];
  return value.map((item) =>
    typeof item === "string" ? item : (item as { id: string }).id,
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapRecordToTrainingVersion(record: any): TrainingVersion {
  return {
    id: record.id,
    trainingId: record.trainingId ?? record.training_id,
    title: record.title,
    description: record.description ?? null,
    rank: record.rank,
    isAvailable: record.isAvailable ?? record.is_available ?? false,
    topicIds: relationIds(record.topicIds ?? record.topic_ids),
    relatedTopicIds: relationIds(
      record.relatedTopicIds ?? record.related_topic_ids,
    ),
    created: record.created ?? record.created_at ?? "",
    updated: record.updated ?? record.updated_at ?? "",
  };
}

function trainingVersionFormToPayload(
  data: TrainingVersionFormData,
): Record<string, unknown> {
  return {
    trainingId: data.trainingId,
    title: data.title,
    description: data.description,
    rank: data.rank,
    isAvailable: data.isAvailable,
    topicIds: data.topicIds ?? [],
    relatedTopicIds: data.relatedTopicIds ?? [],
  };
}

export const trainingVersionRepository: RepositoryInterface<
  TrainingVersion,
  TrainingVersionFormData
> = {
  async getAll(trainingId: string) {
    const records = await pb.collection(collection).getFullList({
      filter: `trainingId = "${trainingId}"`,
      sort: "rank",
    });
    return records.map(mapRecordToTrainingVersion);
  },
  async getOne(id: string | number) {
    const record = await pb.collection(collection).getOne(String(id));
    return mapRecordToTrainingVersion(record);
  },
  async addUpdate(data: TrainingVersionFormData, id: string | null) {
    const payload = trainingVersionFormToPayload(data);
    let record;
    if (id) {
      record = await pb.collection(collection).update(String(id), payload);
    } else {
      record = await pb.collection(collection).create(payload);
    }
    return mapRecordToTrainingVersion(record);
  },
  async delete(id: string | number) {
    await pb.collection(collection).delete(String(id));
  },
  async updateOrder(list: TrainingVersion[]) {
    const updates = list.map((trainingVersion, index) => ({
      id: trainingVersion.id,
      rank: index + 1,
    }));
    await pb.send("/api/reorder", {
      method: "POST",
      body: {
        collection,
        data: updates,
      },
    });
  },
};
